import type { SceneStimulusEntry } from "../config/scene-stimulus-schema";
import { shuffled } from "../core/collections";
import { createDirectionColorMap } from "../core/direction-colors";
import { deriveSeed, DeterministicRandom } from "../core/random";
import { DIRECTIONS } from "../core/types";
import { buildFiveDecisionAudioSchedule } from "../stimuli/audio-schedule";
import type { SceneCandidate, SceneDecision, SceneTrainingMode } from "./types";

export function createSceneOrder(
  scenes: readonly SceneStimulusEntry[],
  seed: number,
): SceneStimulusEntry[] {
  if (scenes.length !== 4) throw new Error("The current paradigm requires exactly four enabled scenes");
  const indoor = scenes.filter((scene) => scene.environmentType === "indoor");
  const outdoor = scenes.filter((scene) => scene.environmentType === "outdoor");
  if (indoor.length !== 2 || outdoor.length !== 2) {
    throw new Error("The current paradigm requires two indoor and two outdoor scenes");
  }
  return shuffled(scenes, new DeterministicRandom(deriveSeed(seed, "formal-scene-order")));
}

export function createSceneDecisionBlocks(
  scenes: readonly SceneStimulusEntry[],
  seed: number,
  mode: SceneTrainingMode,
): readonly (readonly SceneDecision[])[] {
  const ordered = createSceneOrder(scenes, seed);
  const decisionCount = mode === "formal" ? 5 : 1;
  return ordered.map((targetScene, sceneIndex) => {
    const audio = buildFiveDecisionAudioSchedule(targetScene.audio, seed, targetScene.id);
    const photoOrders = new Map(scenes.map((scene) => [
      scene.id,
      shuffled(scene.photos, new DeterministicRandom(deriveSeed(seed, `photos:${targetScene.id}:${scene.id}`))),
    ]));
    return Array.from({ length: decisionCount }, (_, decisionIndex) => {
      const directionOrder = shuffled(
        DIRECTIONS,
        new DeterministicRandom(deriveSeed(seed, `directions:${targetScene.id}:${decisionIndex}`)),
      );
      const sceneOrder = shuffled(
        scenes,
        new DeterministicRandom(deriveSeed(seed, `candidates:${targetScene.id}:${decisionIndex}`)),
      );
      const candidates = sceneOrder.map((scene, candidateIndex): SceneCandidate => {
        const photo = photoOrders.get(scene.id)?.[decisionIndex];
        const direction = directionOrder[candidateIndex];
        if (!photo || !direction) throw new Error("Failed to create a complete scene decision");
        return {
          direction,
          sceneId: scene.id,
          sceneName: scene.displayName,
          photo,
          isTarget: scene.id === targetScene.id,
        };
      });
      const scheduledAudio = audio[decisionIndex];
      if (!scheduledAudio) throw new Error("Missing scheduled scene audio");
      return {
        sceneIndex,
        decisionIndex,
        targetScene,
        audio: scheduledAudio,
        candidates,
        directionColors: createDirectionColorMap(seed, "combined", sceneIndex * 10 + decisionIndex),
      };
    });
  });
}

export function selectTwoChoiceCandidates(
  decision: SceneDecision,
  seed: number,
): readonly SceneCandidate[] {
  const target = decision.candidates.find((candidate) => candidate.isTarget);
  const distractors = decision.candidates.filter((candidate) => !candidate.isTarget);
  if (!target || distractors.length !== 3) throw new Error("Two-choice assistance requires four candidates");
  const random = new DeterministicRandom(
    deriveSeed(seed, `scene-assistance:${decision.targetScene.id}:${decision.decisionIndex}`),
  );
  const distractor = distractors[random.nextInt(distractors.length)];
  if (!distractor) throw new Error("Failed to select assistance distractor");
  return [target, distractor].sort(
    (left, right) => DIRECTIONS.indexOf(left.direction) - DIRECTIONS.indexOf(right.direction),
  );
}

